/* eslint-disable no-underscore-dangle */
import {
  EventDataService,
  EventTemplateService,
  StatusService,
  TagService,
  TagTypeService,
} from '@pingum/api-services';
import { useAuth0 } from '@auth0/auth0-react';
import { getEventData } from '@pingum/app-state-management/eventData/actions';
import * as eventDataActionTypes from '@pingum/app-state-management/eventData/types';
import { searchEventTemplates } from '@pingum/app-state-management/eventTemplate/actions';
import * as eventTemplateActionTypes from '@pingum/app-state-management/eventTemplate/types';
import { getStatuses } from '@pingum/app-state-management/status/actions';
import * as statusActionTypes from '@pingum/app-state-management/status/types';
import { getTags } from '@pingum/app-state-management/tag/actions';
import * as tagActionTypes from '@pingum/app-state-management/tag/types';
import { getTagTypes } from '@pingum/app-state-management/tagType/actions';
import * as tagTypeActionTypes from '@pingum/app-state-management/tagType/types';
import {
  getTenantUserRequests,
  requestTenant,
} from '@pingum/app-state-management/tenant/actions';
import { createBrowserHistory } from 'history';
import PropTypes from 'prop-types';
import * as React from 'react';
import { clarity } from 'react-microsoft-clarity';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import 'stream-chat-react/dist/css/v2/index.css';

import { formLabelClasses } from '@mui/material';
import { getUserSubscription } from '@pingum/app-state-management/userSubscription/actions';
import moment from 'moment';
import { StreamChat } from 'stream-chat';
import { Chat } from 'stream-chat-react';
import AppContainer from './navigation/AppContainer';
import { setTimezones } from './redux/app/actions';
import {
  getAllBarnHorses,
  getBarns,
  getBarnUsers,
  getSubscriptions,
} from './redux/barn/actions';
import { getEventTemplateReminders } from './redux/eventTemplateReminder/actions';
import {
  getAllHorseLocations,
  getAllLocations,
  setSelectedLocation,
} from './redux/location/actions';
import { getLocationBoards } from './redux/locationBoard/actions';
import { getStreamUser } from './redux/stream/actions';
import {
  checkEmail,
  getMeBarnUser,
  getMeHorseAccess,
  getMyTenants,
} from './redux/user/actions';
import {
  capturePostHogEvent,
  consumePostHogInteractiveLogin,
  identifyPostHogUser,
  initializePostHog,
  resetPostHogIfIdentified,
  synchronizePostHogRoute,
  synchronizePostHogTenant,
} from './utils/posthog';
import restoreStartupTokens from './utils/restoreStartupTokens';
import { userHasFullAppRole } from './utils/roleAccessHelper';
import { getTenantFromUrl } from './utils/urlHelper';
import useClarityUser from './utils/useClarityUser';

moment.updateLocale('en', {
  week: {
    dow: (window.localStorage.getItem('planning_week_style') || 'isoWeek') === 'isoWeek'
      ? 1 // First day of week is Monday
      : 0,
    doy: 4, // First week of year must contain 4 January (7 + 1 - 4)
  },
});

export const history = createBrowserHistory();
const apiKey = process.env.REACT_APP_STREAM_KEY || 'agrvjewmw5nx';
const selectedLocationResetPaths = ['/settings/tasks'];
const CORE_DATA_REFRESH_INTERVAL_MS = 3 * 60 * 60 * 1000;

const AppComponent = ({
  pages,
  runtimeEnvironment,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const tenant = getTenantFromUrl();
  const {
    pathname,
    search,
  } = useLocation();

  // auth0 state
  const {
    isLoading: isLoadingAuth0,
    isAuthenticated,
    user,
    getAccessTokenSilently,
    getIdTokenClaims,
  } = useAuth0();

  React.useEffect(() => {
    clarity.init('i9yzdty460');
  }, [])

  const [selectedBarnId, setSelectedBarnId] = React.useState(null);
  const [chatConnected, setChatConnected] = React.useState(false);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const lastCoreDataRefreshAtRef = React.useRef(0);
  const isRefreshingCoreDataRef = React.useRef(false);
  const authenticatedSessionUserIdRef = React.useRef(null);
  const [chatClient] = React.useState(StreamChat.getInstance(apiKey, {
    enableInsights: true,
    enableWSFallback: true,
  }))

  // redux state
  const isFetchingUserTenants = useSelector((state) => state.user.isFetchingUserTenants);
  const isFetchingUser = useSelector((state) => state.user.isFetchingUser);
  const isFetchingMeHorseAccess = useSelector((state) => state.user.isFetchingMeHorseAccess);
  const permissions = useSelector((state) => state.user.permissions);
  const isClient = useSelector((state) => state.user.isClient);
  const checkEmailUser = useSelector((state) => state.user.checkEmailUser);
  const isCheckingEmail = useSelector((state) => state.user.isCheckingEmail);
  const isFetchingTenant = useSelector((state) => state.tenant.isFetchingTenant);
  const isFetchingBarnUsers = useSelector((state) => state.barn.isFetchingBarnUsers);
  const selectedBarn = useSelector((state) => state.barn.selectedBarn);
  const selectedLocation = useSelector((state) => state.location.selectedLocation);
  const barnUser = useSelector((state) => state.user.barnUser);
  const streamUser = useSelector((state) => state.stream.streamUser);
  const authenticatedUserId = user && user.sub;
  const authenticatedUserEmail = user && user.email;
  const authenticatedUserName = user && user.name;
  const selectedBarnPostHogId = selectedBarn && selectedBarn.id;

  useClarityUser({
    isAuthenticated,
    isLoadingAuth0,
    userId: authenticatedUserId,
    pathname,
  });

  // global loading state - when true will show loading placeholder
  const isLoading = isFetchingUserTenants || isFetchingUser || isFetchingTenant
    || isFetchingBarnUsers || isFetchingMeHorseAccess || isLoadingAuth0 || isCheckingEmail;
  const eventTemplateSearchRequest = React.useMemo(() => {
    if (!(selectedBarn && selectedBarn.barnMetadata)) {
      return null;
    }

    return {
      tagConditions: [[
        selectedBarn.barnMetadata.horseTaskTypeTagId,
        selectedBarn.barnMetadata.barnTaskTypeTagId,
        selectedBarn.barnMetadata.eventTaskTypeTagId,
      ]],
    };
  }, [selectedBarn]);
  const selectedBarnRef = React.useRef(selectedBarn);
  const eventTemplateSearchRequestRef = React.useRef(eventTemplateSearchRequest);

  React.useEffect(() => {
    selectedBarnRef.current = selectedBarn;
  }, [selectedBarn]);

  React.useEffect(() => {
    eventTemplateSearchRequestRef.current = eventTemplateSearchRequest;
  }, [eventTemplateSearchRequest]);

  React.useEffect(() => {
    synchronizePostHogRoute({
      pathname,
      search,
    });
  }, [pathname, search]);

  React.useEffect(() => {
    if (isLoadingAuth0) {
      return;
    }

    initializePostHog(runtimeEnvironment);

    if (isAuthenticated && authenticatedUserId) {
      const identified = identifyPostHogUser({
        id: authenticatedUserId,
        email: authenticatedUserEmail,
        name: authenticatedUserName,
      });
      const isInteractiveLogin = consumePostHogInteractiveLogin();

      if (identified && isInteractiveLogin) {
        capturePostHogEvent('user_logged_in', { authentication_provider: 'auth0' });
      }

      if (identified && authenticatedSessionUserIdRef.current !== authenticatedUserId) {
        capturePostHogEvent('authenticated_session_started', { authentication_provider: 'auth0' });
        authenticatedSessionUserIdRef.current = authenticatedUserId;
      }
      return;
    }

    consumePostHogInteractiveLogin();
    authenticatedSessionUserIdRef.current = null;
    resetPostHogIfIdentified();
  }, [
    authenticatedUserEmail,
    authenticatedUserId,
    authenticatedUserName,
    isAuthenticated,
    isLoadingAuth0,
    runtimeEnvironment,
  ]);

  React.useEffect(() => {
    synchronizePostHogTenant({
      isAuthenticated: !isLoadingAuth0 && isAuthenticated,
      selection: selectedBarn,
      tenantId: selectedBarnPostHogId,
      userId: authenticatedUserId,
    });
  }, [authenticatedUserId, isAuthenticated, isLoadingAuth0, selectedBarn, selectedBarnPostHogId]);

  const loadSelectedBarnCoreData = React.useCallback(() => {
    const currentSelectedBarn = selectedBarnRef.current;
    const currentEventTemplateSearchRequest = eventTemplateSearchRequestRef.current;

    if (!selectedBarnId || !currentSelectedBarn || !currentEventTemplateSearchRequest) {
      return;
    }

    dispatch(getAllBarnHorses(selectedBarnId));
    dispatch(getAllLocations()).then((data) => {
      if (data.length === 0) {
        navigate('/onboarding')
      }
    });
    dispatch(getLocationBoards());
    dispatch(getStatuses(false));
    dispatch(getEventData(false));
    dispatch(getTags(false, false));
    dispatch(getTagTypes(false, true));
    dispatch(getSubscriptions(currentSelectedBarn));
    dispatch(getEventTemplateReminders());
    dispatch(searchEventTemplates(false, currentEventTemplateSearchRequest));
  }, [dispatch, navigate, selectedBarnId]);

  const refreshCoreDataSilently = React.useCallback(async () => {
    const currentSelectedBarn = selectedBarnRef.current;
    const currentEventTemplateSearchRequest = eventTemplateSearchRequestRef.current;

    if (!selectedBarnId || !currentSelectedBarn || !currentEventTemplateSearchRequest || isRefreshingCoreDataRef.current) {
      return;
    }

    isRefreshingCoreDataRef.current = true;

    const runSilentRequest = async (request, onSuccess) => {
      try {
        const response = await request;
        onSuccess(response);
      } catch (error) {
        console.error(error);
      }
    };

    try {
      await Promise.allSettled([
        dispatch(getBarnUsers({ silent: true })),
        dispatch(getAllBarnHorses(selectedBarnId, new Date(), new Date(), '', { silent: true })),
        dispatch(getAllLocations({ silent: true })),
        dispatch(getLocationBoards({ silent: true })),
        dispatch(getEventTemplateReminders({ silent: true })),
        Promise.resolve(dispatch(getSubscriptions(currentSelectedBarn))),
        runSilentRequest(StatusService.getStatuses(false), (statuses) => {
          dispatch({
            type: statusActionTypes.STATUS_INCLUDE_ARCHIVED_UPDATED,
            includeArchived: false,
          });
          dispatch({
            type: statusActionTypes.STATUSES_SUCCESS,
            statuses,
          });
        }),
        runSilentRequest(EventDataService.getEventData(false), (eventData) => {
          dispatch({
            type: eventDataActionTypes.EVENT_DATA_INCLUDE_ARCHIVED_UPDATED,
            includeArchived: false,
          });
          dispatch({
            type: eventDataActionTypes.EVENT_DATA_SUCCESS,
            eventData,
          });
        }),
        runSilentRequest(TagService.getTags(false, false), (tags) => {
          dispatch({
            type: tagActionTypes.TAG_SUCCESS,
            tags,
          });
        }),
        runSilentRequest(TagTypeService.getTagTypes(false, true), (tagTypes) => {
          dispatch({
            type: tagTypeActionTypes.TAG_INCLUDE_ARCHIVED_UPDATED,
            includeArchived: false,
          });
          dispatch({
            type: tagTypeActionTypes.TAG_TYPE_SUCCESS,
            tagTypes,
          });
        }),
        runSilentRequest(
          EventTemplateService.searchEventTemplates(false, currentEventTemplateSearchRequest),
          (eventTemplates) => {
            dispatch({
              type: eventTemplateActionTypes.EVENT_TEMPLATE_INCLUDE_ARCHIVED_UPDATED,
              includeArchived: false,
            });
            dispatch({
              type: eventTemplateActionTypes.EVENT_TEMPLATE_SEARCH_SUCCESS,
              eventTemplates,
            });
          },
        ),
      ]);
      lastCoreDataRefreshAtRef.current = Date.now();
    } finally {
      isRefreshingCoreDataRef.current = false;
    }
  }, [dispatch, selectedBarnId]);

  // on path change
  React.useEffect(() => {
    const fetchData = async () => {
      if (selectedLocationResetPaths.indexOf(pathname) >= 0) {
        // remove any selected location on these paths
        dispatch(setSelectedLocation({}));
      }
      // Reauth every hour
      let lastAuthTimestamp = window.localStorage.getItem('auth_timestamp') || new Date().getTime();
      lastAuthTimestamp = parseInt(lastAuthTimestamp, 10);
      if (new Date().getTime() - lastAuthTimestamp > 3600000) {
        getAccessTokenSilently({ cacheMode: 'off' });
        const accessToken = await getAccessTokenSilently({ cacheMode: 'off' });
        global.window.localStorage.setItem('access_token', accessToken);
        global.window.localStorage.setItem('auth_timestamp', new Date().getTime());
      }
    }
    fetchData();
  }, [pathname]);

  React.useEffect(() => {
    const listenerNewMessage = chatClient?.on((event) => {
      if (event.total_unread_count !== undefined) {
        setUnreadCount(event.total_unread_count)
      }
    });

    return () => {
      if (listenerNewMessage) {
        listenerNewMessage.unsubscribe();
      }
    };
  }, [chatClient]);

  // on authenticated change or token retrieved
  React.useEffect(() => {
    const fetchData = async () => {
      if (isAuthenticated) {
        const accessToken = await getAccessTokenSilently();
        const idToken = await getIdTokenClaims();
        global.window.localStorage.setItem('access_token', accessToken);
        global.window.localStorage.setItem('id_token', idToken.__raw);
        global.window.localStorage.setItem('auth_timestamp', new Date().getTime());
        if (tenant) {
          dispatch(requestTenant(tenant));
          dispatch(getBarns());
          dispatch(getBarnUsers());
          dispatch(getMeHorseAccess());
          dispatch(checkEmail(user.email));
          dispatch(getUserSubscription(user.sub));
          dispatch(getTenantUserRequests(tenant));
          dispatch(getAllHorseLocations())
        }
        dispatch(await getMeBarnUser({ history }));
        dispatch(getMyTenants());
        dispatch(getStreamUser());

        if (window.location.pathname.indexOf(pathname) < 0) {
          navigate(pathname)
        }
      } else if (!isLoadingAuth0) {
        global.window.localStorage.removeItem('id_token');
        global.window.localStorage.removeItem('access_token');
        global.window.localStorage.removeItem('auth_timestamp');
      }
    }
    fetchData();
  }, [isAuthenticated]);

  React.useEffect(() => {
    if (barnUser && streamUser && streamUser.auth0UserId && streamUser.streamToken) {
      // eslint-disable-next-line no-console
      chatClient.connectUser({
        id: streamUser.auth0UserId.replace('|', '').replaceAll('.', ''),
        image: (barnUser && barnUser.picture),
        name: (barnUser && barnUser.userFullName) || 'Unknown User',
      }, streamUser.streamToken).then((connection) => {
        setChatConnected(connection && connection.connection_id);
        setUnreadCount(connection?.me?.total_unread_count)
      });
      if (clarity.hasStarted() && selectedBarn && selectedBarn.tenant) {
        clarity.setTag('tenant', selectedBarn.tenant);
      }
    } else if (chatClient && chatConnected) {
      // eslint-disable-next-line no-console
      console.log('Disconnecting from chat')
      chatClient.disconnectUser();
      setChatConnected(false);
    }
    return () => {
      if (chatClient && chatConnected) {
        // eslint-disable-next-line no-console
        console.log('Disconnecting from chat')
        chatClient.disconnectUser();
        setChatConnected(formLabelClasses);
      }
    };
  }, [barnUser, streamUser])

  React.useEffect(() => {
    if (!selectedBarnId) {
      return;
    }

    lastCoreDataRefreshAtRef.current = Date.now();
    loadSelectedBarnCoreData();
  }, [loadSelectedBarnCoreData, selectedBarnId]);

  React.useEffect(() => {
    if (!selectedBarnId) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      refreshCoreDataSilently();
    }, CORE_DATA_REFRESH_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [refreshCoreDataSilently, selectedBarnId]);

  React.useEffect(() => {
    const fetchData = async () => {
      if (selectedBarn && selectedBarn.id && selectedBarn.id !== selectedBarnId) {
        setSelectedBarnId(selectedBarn.id)
      }
    }
    fetchData();
  }, [selectedBarn]);

  React.useEffect(() => {
    dispatch(setTimezones(selectedLocation && selectedLocation.timezone));
  }, [selectedLocation]);

  React.useEffect(() => {
    restoreStartupTokens({
      getAccessTokenSilently,
      getIdTokenClaims,
    });
  }, []);

  const canAccessFullApp = permissions.owner || permissions.admin || permissions.staff;
  const checkEmailUserHasFullAppRole = userHasFullAppRole(checkEmailUser && checkEmailUser.roles);
  const canAccessBoardOnly = isClient && !!tenant && !canAccessFullApp && !checkEmailUserHasFullAppRole;

  return (
    <AppContainer isLoading={isLoading} navigate={navigate} chatClient={chatConnected && chatClient} unreadCount={unreadCount}>
      <Routes>
        {/* Auth or app state pages */}
        <Route exact path="/callback" element={<div>callback</div>} title="Login | Questri" />
        <Route exact path="/confirm-email" element={<pages.ConfirmEmailPage />} title="Please confirm your email address | Questri" /> {/* TODO is that page still needed? */}
        <Route exact path="/logout" element={<pages.LogoutPage />} title="Logged out | Questri" />
        <Route exact path="/authorization-error" element={<pages.AuthorizationError />} title="Unauthorized | Questri" />

        {/* Account pages */}
        <Route exact path="/settings/profile" element={<pages.AccountProfilePage navigate={navigate} />} title="Account Profile | Questri" />
        <Route exact path="/settings/notifications" element={<pages.AccountNotificationsPage navigate={navigate} />} title="Account Notifications | Questri" />

        {/* Public Schedules */}
        <Route exact path="/schedules/:id" element={<pages.SchedulePage navigate={navigate} />} title="Calendar | Questri" />
        <Route exact path="/invoices/:id" element={<pages.BillingInvoicePage navigate={navigate} />} title="Invoice | Questri" />
        <Route exact path="/public/horses/:id/shared/:shareId" element={<pages.PublicHorsePage navigate={navigate} />} title="Horse Profile | Questri" />

        {
          canAccessFullApp && (
            <>
              {/* Calendar */}
              <Route exact path="/" element={<pages.CalendarPage navigate={navigate} />} title="Calendar | Questri" />
              <Route exact path="/calendar" element={<pages.CalendarPage navigate={navigate} />} title="Calendar | Questri" />
              <Route exact path="/tasks/planning" element={<pages.CalendarPage navigate={navigate} />} title="Calendar | Questri" /> {/* TODO - <!-- Legacy route --> Remove this */}
              <Route exact path="/tasks/calendar" element={<pages.CalendarPage navigate={navigate} />} title="Calendar | Questri" /> {/* TODO - <!-- Legacy route --> Remove this */}

              {/* Tasks */}
              <Route exact path="/tasks" element={<pages.TasksPage navigate={navigate} permissions={permissions} />} title="All Tasks | Questri" />
              <Route exact path="/tasks/:id" element={<pages.TaskPage navigate={navigate} permissions={permissions} />} title="Task | Questri" />
              <Route exact path="/tasks/analytics" element={<pages.TasksAnalyticsPage navigate={navigate} permissions={permissions} />} title="Task Analytics | Questri" />
              <Route exact path="/tasks/reminders" element={<pages.TaskRemindersPage navigate={navigate} permissions={permissions} />} title="Task Reminders | Questri" />

              {/* Barn and Boards */}
              <Route exact path="/board" element={<pages.BarnBoardPage navigate={navigate} />} title="Barn Board | Questri" />

              {/* Onboarding */}
              <Route exact path="/onboarding" element={<pages.OnboardingPage navigate={navigate} permissions={permissions} />} title="Welcome To Questri | Questri" />

              {/* User */}
              <Route exact path="/users/:id" element={<pages.UserPage navigate={navigate} permissions={permissions} />} title="View User | Questri" />

              {/* Billing */}
              <Route path="/billing/quickbooks/how-it-works" element={<pages.BillingQuickBooksPage guide navigate={navigate} permissions={permissions} />} />
              <Route path="/billing/quickbooks" element={<pages.BillingQuickBooksPage navigate={navigate} permissions={permissions} />} />
              {
                (permissions.owner || permissions.admin)
                  ? (
                    <Route exact path="/billing/dashboard" element={<pages.BillingDashboardPage navigate={navigate} permissions={permissions} />} title="Billing Dashboard | Questri" />
                  )
                  : (
                    <Route exact path="/billing/dashboard" element={<pages.NoBillingAccessPage navigate={navigate} permissions={permissions} />} title="Billing Dashboard | Questri" />
                  )
              }
              {
                (permissions.owner || permissions.admin)
                  ? (
                    <Route exact path="/billing/transactions" element={<pages.BillingBillablesPage navigate={navigate} permissions={permissions} />} title="Billing Billables | Questri" />
                  )
                  : (
                    <Route exact path="/billing/dashboard" element={<pages.NoBillingAccessPage navigate={navigate} permissions={permissions} />} title="Billing Dashboard | Questri" />
                  )
              }
              {
                (permissions.owner || permissions.admin)
                  ? (
                    <Route exact path="/billing/upcoming" element={<pages.BillingRecurringBillablesPage navigate={navigate} permissions={permissions} />} title="Recurring Billables | Questri" />
                  )
                  : (
                    <Route exact path="/billing/dashboard" element={<pages.NoBillingAccessPage navigate={navigate} permissions={permissions} />} title="Billing Dashboard | Questri" />
                  )
              }
              {
                (permissions.owner || permissions.admin)
                  ? (
                    <Route exact path="/billing/invoices" element={<pages.BillingInvoicesPage navigate={navigate} permissions={permissions} />} title="Billing Invoices | Questri" />
                  )
                  : (
                    <Route exact path="/billing/dashboard" element={<pages.NoBillingAccessPage navigate={navigate} permissions={permissions} />} title="Billing Dashboard | Questri" />
                  )
              }
              {
                (permissions.owner || permissions.admin)
                  ? (
                    <Route exact path="/billing/payments" element={<pages.BillingManagePaymentsPage navigate={navigate} permissions={permissions} />} title="Billing Payments | Questri" />
                  )
                  : (
                    <Route exact path="/billing/dashboard" element={<pages.NoBillingAccessPage navigate={navigate} permissions={permissions} />} title="Billing Dashboard | Questri" />
                  )
              }
              {
                (permissions.owner || permissions.admin)
                  ? (
                    <Route exact path="/billing/payments" element={<pages.BillingManagePaymentsPage navigate={navigate} permissions={permissions} />} title="Manage Payments | Questri" />
                  )
                  : (
                    <Route exact path="/billing/dashboard" element={<pages.NoBillingAccessPage navigate={navigate} permissions={permissions} />} title="Billing Dashboard | Questri" />
                  )
              }
              {
                (permissions.owner || permissions.admin)
                  ? (
                    <Route exact path="/billing/settings" element={<pages.BillingSettingsPage navigate={navigate} permissions={permissions} />} title="Billing Settings | Questri" />
                  )
                  : (
                    <Route exact path="/billing/dashboard" element={<pages.NoBillingAccessPage navigate={navigate} permissions={permissions} />} title="Billing Dashboard | Questri" />
                  )
              }
              <Route exact path="/billing/payments/connected" element={<pages.BillingPaymentsConnectedPage navigate={navigate} permissions={permissions} />} title="Payments Connected | Questri" />

              {/* Horses */}
              <Route exact path="/horses" element={<pages.HorsesPage navigate={navigate} permissions={permissions} />} title="View Horses | Questri" />
              <Route exact path="/horses/:id" element={<pages.HorsePage navigate={navigate} permissions={permissions} />} title="View and Manage Horse | Questri" />
              <Route exact path="/horses/:id/notes/:noteId" element={<pages.HorseNotePage navigate={navigate} permissions={permissions} />} title="View and Manage Horse Note | Questri" />

              {/* Settigns pages */}
              <Route exact path="/settings/feeds" element={<pages.FeedSettingsPage navigate={navigate} permissions={permissions} />} title="Feed Settings | Questri" />
              <Route exact path="/settings/feeds/:id" element={<pages.FeedPage navigate={navigate} permissions={permissions} />} title="Feed | Questri" />
              <Route exact path="/settings/locations" element={<pages.LocationSettingsPage navigate={navigate} permissions={permissions} />} title="Location Settings | Questri" />
              <Route exact path="/settings/locations/:id" element={<pages.LocationPage navigate={navigate} permissions={permissions} />} title="Location | Questri" />
              <Route exact path="/settings/tasks" element={<pages.TaskDefinitionSettingsPage navigate={navigate} permissions={permissions} />} title="Task Settings | Questri" />
              <Route exact path="/settings/tasks/reminders" element={<pages.TaskDefinitionSettingsPage navigate={navigate} permissions={permissions} />} title="Task Settings - Reminders  | Questri" />
              <Route exact path="/settings/tasks/tags" element={<pages.TaskDefinitionSettingsPage navigate={navigate} permissions={permissions} />} title="Task Settings - Tags  | Questri" />
              <Route exact path="/settings/tasks/statuses" element={<pages.TaskDefinitionSettingsPage navigate={navigate} permissions={permissions} />} title="Task Settings - Task Statuses  | Questri" />
              <Route exact path="/settings/tasks/new" element={<pages.TaskDetailSettingsPage navigate={navigate} permissions={permissions} />} title="Task Settings - New Task Type  | Questri" />
              <Route exact path="/settings/tasks/:id" element={<pages.TaskDetailSettingsPage navigate={navigate} permissions={permissions} />} title="Task Settings - Edit Task Type | Questri" />
              <Route exact path="/settings/calendar" element={<pages.CalendarEventSettingsPage navigate={navigate} permissions={permissions} />} title="Calendar Settings | Questri" />
              <Route exact path="/settings/calendar/new" element={<pages.CalendarEventDetailSettingsPage navigate={navigate} permissions={permissions} />} title="Calendar Settings - New Calendar Type  | Questri" />
              <Route exact path="/settings/calendar/:id" element={<pages.CalendarEventDetailSettingsPage navigate={navigate} permissions={permissions} />} title="Calendar Settings - Edit Calendar Type | Questri" />

              { /* Stream page */}
              <Route
                exact
                path="/chat"
                element={(
                  <Chat
                    client={chatClient}
                    defaultLanguage="en"
                    theme="team light"
                  >{chatConnected && chatClient && <pages.StreamPage navigate={navigate} permissions={permissions} />}
                  </Chat>
                )}
                title="Questri Chat | Questri"
              />

              {(permissions.owner || permissions.admin) && <Route exact path="/settings/users" element={<pages.UserManagementPage tenantName={tenant} navigate={navigate} permissions={permissions} />} title="User Management Settings | Questri" />}
              {(permissions.owner || permissions.admin) && <Route exact path="/settings/users/requests" element={<pages.RequestsToJoinPage tenantName={tenant} navigate={navigate} permissions={permissions} />} title="User Requests to Join | Questri" />}
              { /* TODO should user page be under permission restriction? It is in global search so maybe we have a non admin read only view? */}
              <Route exact path="/settings/admin/users/:id" element={<pages.UserPage tenantName={tenant} navigate={navigate} permissions={permissions} />} title="Manage User Settings | Questri" />
              {permissions.owner && <Route exact path="/settings/admin" element={<pages.AdminSettingsPage navigate={navigate} permissions={permissions} />} title="Admin Settings | Questri" />}
            </>
          )
        }

        {
          canAccessBoardOnly && (
            <>
              <Route exact path="/" element={<div />} title="Questri | Limited Access" />
              <Route exact path="/board" element={<pages.BarnBoardPage navigate={navigate} readOnly />} title="Barn Board | Questri" />
              <Route exact path="/tasks/planning" element={<pages.BarnBoardPage navigate={navigate} readOnly />} title="Barn Board | Questri" />
              <Route exact path="/tasks/calendar" element={<pages.BarnBoardPage navigate={navigate} readOnly />} title="Barn Board | Questri" />
            </>
          )
        }

        {/* TODO - Catch All Not Found Page */}
        {
          !canAccessBoardOnly && (
            <Route element={<div>Not Found</div>} />
          )
        }
      </Routes>
    </AppContainer>
  );
};

AppComponent.propTypes = {
  pages: PropTypes.object,
  runtimeEnvironment: PropTypes.object,
};

AppComponent.defaultProps = {
  runtimeEnvironment: {},
  pages: {
    // Auth and Other Pagess
    ConfirmEmailPage: React.lazy(() => import('./pages/auth/ConfirmEmail')),
    LogoutPage: React.lazy(() => import('./pages/auth/LogoutPage')),
    AuthorizationError: React.lazy(() => import('./pages/auth/AuthorizationError')),

    // Barn Pages
    BarnBoardPage: React.lazy(() => import('./pages/barn/Board')),

    // User Pages
    UserPage: React.lazy(() => import('./pages/settings/UserPage')),

    // Horse Pages
    HorsesPage: React.lazy(() => import('./pages/horse/Horses')),
    HorsePage: React.lazy(() => import('./pages/horse/Horse')),
    HorseNotePage: React.lazy(() => import('./pages/horse/HorseNote')),
    PublicHorsePage: React.lazy(() => import('./pages/horse/PublicHorse')),

    // Calendar Page
    CalendarPage: React.lazy(() => import('./pages/calendar/Calendar')),

    // Task Pages
    TaskRemindersPage: React.lazy(() => import('./pages/task/TaskReminders')),
    TasksPage: React.lazy(() => import('./pages/task/Tasks')),
    TasksAnalyticsPage: React.lazy(() => import('./pages/task/Analytics')),
    TaskPage: React.lazy(() => import('./pages/task/Task')),

    // Schedules Pages
    SchedulePage: React.lazy(() => import('./pages/schedule/SchedulePage')),

    // Billing Pages
    BillingDashboardPage: React.lazy(() => import('./pages/billing/components/Dashboard')),
    BillingBillablesPage: React.lazy(() => import('./pages/billing/components/Billables')),
    BillingRecurringBillablesPage: React.lazy(() => import('./pages/billing/components/RecurringBillables')),
    BillingSettingsPage: React.lazy(() => import('./pages/billing/components/Settings')),
    BillingQuickBooksPage: React.lazy(() => import('./pages/billing/components/QuickBooks')),
    NoBillingAccessPage: React.lazy(() => import('./pages/billing/components/NoBillingAccess')),
    BillingInvoicesPage: React.lazy(() => import('./pages/billing/components/Invoices')),
    BillingInvoicePage: React.lazy(() => import('./pages/billing/components/Invoice')),
    BillingManagePaymentsPage: React.lazy(() => import('./pages/billing/components/ManagePayments')),
    BillingPaymentsConnectedPage: React.lazy(() => import('./pages/billing/components/ManagePayments/components/Connected')),

    // Stream Pages
    StreamPage: React.lazy(() => import('./pages/stream')),

    // Account pages
    AccountProfilePage: React.lazy(() => import('./pages/account/Profile')),
    AccountNotificationsPage: React.lazy(() => import('./pages/account/Notifications')),

    // Settings Pages
    LocationSettingsPage: React.lazy(() => import('./pages/settings/LocationSettingsPage')),
    LocationPage: React.lazy(() => import('./pages/settings/LocationPage')),
    FeedSettingsPage: React.lazy(() => import('./pages/settings/FeedSettingsPage')),
    FeedPage: React.lazy(() => import('./pages/settings/FeedPage')),
    UserManagementPage: React.lazy(() => import('./pages/settings/UserManagementPage')),
    RequestsToJoinPage: React.lazy(() => import('./pages/settings/UserManagementPage/components/RequestsToJoin')),
    AdminSettingsPage: React.lazy(() => import('./pages/settings/AdminSettingsPage')),
    TaskDefinitionSettingsPage: React.lazy(() => import('./pages/settings/TaskPage')),
    TaskDetailSettingsPage: React.lazy(() => import('./pages/settings/TaskDetailSettingsPage')),
    CalendarEventSettingsPage: React.lazy(() => import('./pages/settings/CalendarEventSettingsPage')),
    CalendarEventDetailSettingsPage: React.lazy(() => import('./pages/settings/CalendarEventDetailSettingsPage')),

    // Onboarding
    OnboardingPage: React.lazy(() => import('./pages/onboarding')),
  },
};

export default AppComponent;
